// User-facing error display helper.
//
// Backend errors fall into two buckets:
//
// 1. **Expected / user-actionable** — validation errors, auth errors,
//    rate-limit, "you don't have permission". These carry meaningful
//    codes the user (or operator) can react to. Show the backend's
//    message verbatim.
//
// 2. **Everything else** — DB failures, panics, unhandled provider
//    errors, transient timeouts. Surfacing internals to the user is
//    noisy ("pq: connection refused") and a tiny information leak.
//    Render "Something went wrong" + log the real error to the
//    browser console (which the admin can inspect + which Axiom
//    captures server-side already via slog).
//
// To add a new user-facing code, drop it into SAFE_CODES. Default
// stance is "hide details" — opt-in, not opt-out.

import { ConvexError } from 'convex/values';
import { APIError } from './api';

// Backend error codes that ARE safe to render verbatim to the user.
// Everything else collapses to "Something went wrong" + console.error.
const SAFE_CODES = new Set<string>([
  // Validation
  'BAD_REQUEST',
  'INVALID_EMAIL',
  'INVALID_PRICE',
  'INVALID_TIER',
  'INVALID_PLAN',
  'INVALID_COMBO',
  'MISSING_FIELDS',
  'MISSING_TOKEN',
  'WEAK_PASSWORD',
  'TERMS_REQUIRED',
  // Auth / authz
  'UNAUTHORIZED',
  'INVALID_CREDENTIALS',
  'INVALID_TOKEN',
  'INVALID_TOTP',
  'SESSION_REVOKED',
  'PREMIUM_REQUIRED',
  // Resource state
  'USER_NOT_FOUND',
  'EMAIL_TAKEN',
  'EMAIL_LINKED_ELSEWHERE',
  'ALREADY_DELETED',
  'ACCOUNT_GONE',
  'TOKEN_NOT_FOUND',
  'TOKEN_EXPIRED',
  // Feature gates
  'SIGNUPS_DISABLED',
  'STRIPE_NOT_CONFIGURED',
  'NO_SUBSCRIPTION',
  // Throttling
  'RATE_LIMITED',
  // Creator campaigns (MKT)
  'BAD_SLUG',
  'SLUG_NOT_FOUND',
  'CAMPAIGN_EXISTS',
  'BAD_FEE',
  'NOT_FOUND',
  'BAD_URL',
  'BAD_BONUS',
  'ALREADY_HIT',
  'ALREADY_PAID',
]);

const GENERIC = 'Something went wrong. Please try again.';

/**
 * Pull a safe, user-facing message out of an error. Defaults to a
 * generic blurb + logs the underlying error to the console so the
 * operator can debug from devtools (and Axiom retains the server-side
 * stack trace separately).
 */
export function friendlyError(err: unknown, context?: string): string {
  if (err instanceof APIError) {
    if (SAFE_CODES.has(err.code)) return err.message;
    // Unexpected backend error. Log everything; show the generic.
    console.error('[backend error]', context ?? '', err.code, err.message, err);
    return GENERIC;
  }
  if (err instanceof ConvexError) {
    // Convex queries/mutations throw ConvexError with the structured
    // `{ code, message }` shape from src/convex/functions/admin/*.
    const data = err.data as { code?: string; message?: string } | string;
    if (typeof data === 'object' && data && typeof data.code === 'string') {
      if (SAFE_CODES.has(data.code)) return data.message ?? GENERIC;
      console.error('[convex error]', context ?? '', data.code, data.message, err);
      return GENERIC;
    }
    console.error('[convex error]', context ?? '', err);
    return GENERIC;
  }
  if (err instanceof Error) {
    console.error('[unexpected error]', context ?? '', err);
    return GENERIC;
  }
  console.error('[unknown error]', context ?? '', err);
  return GENERIC;
}
